import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AddGeneral from '../add/AddGeneral';
import { Checkbox, Stack, SearchBox, ContextualMenu, Callout, DirectionalHint, DefaultButton, Separator } from '@fluentui/react';
import { PrimaryButton } from '@fluentui/react/lib/Button';
import { IconButton } from "@fluentui/react/lib/Button";
import { Icon } from '@fluentui/react';
import { Spinner, SpinnerSize } from '@fluentui/react';
import GeneralDetails from '../details/GeneralDetails';
import { getList, editStatus } from 'store/actions/generalActions';
import { getUserId, hasValidTokens } from 'utils/authUtils';
import GeneralComment from '../comment/GeneralComment';
import { TooltipHost } from '@fluentui/react';

const GeneralManage = () => {

    const currentUser = getUserId();
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isGeneralDetailsOpen, setIsGeneralDetailsOpen] = useState(false);
    const [isCommentOpen, setIsCommentOpen] = useState(false);

    const [id, setid] = useState(null);
    const dispatch = useDispatch();

    const toggleDrawer = () => setIsDrawerOpen(!isDrawerOpen);
    const closeDrawer = () => {
        setIsDrawerOpen(false);
        setIsGeneralDetailsOpen(false);
        setIsCommentOpen(false);
        setid(null);
    };
    const [target, setTarget] = useState(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [selectedRowId, setSelectedRowId] = useState(null);

    const { loading, generals: generalList = [] } = useSelector(state => state.general);

    const [sortField, setSortField] = useState("createdOn");
    const [sortOrder, setSortOrder] = useState("desc");

    const [searchValue, setSearchValue] = useState('');
    const [searchText, setSearchText] = useState('');
    const [includeCompleted, setincludeCompleted] = useState(false);

    const [pageIndex, setPageIndex] = useState(0);
    const [allGenerals, setAllGenerals] = useState([]);
    const [hasMore, setHasMore] = useState(true);
    const scrollContainerRef = useRef(null);
    const isRefreshingRef = useRef(false);
    const pageSize = 50;
    const [, setSortLoading] = useState(false);
    const filterButtonRef = useRef(null);
    const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
    const [filterResourceTypes, setFilterResourceTypes] = useState([]);
    const [appliedResourceTypes, setAppliedResourceTypes] = useState([]);

    const resourceTypeOptions = [
        { key: 4, text: "Responsible" },
        { key: 1, text: "Accountable" },
        { key: 2, text: "Consult" },
        { key: 3, text: "Inform" }
    ];


    const getUserRole = (item, currentUserId) => {
        if (item.createdBy === currentUserId) return 'creator';
        if (item.responsible?.key === currentUserId) return 'responsible';
        if (item.accountable?.some(a => a.key === currentUserId)) return 'accountable';
        if (item.consult?.some(c => c.key === currentUserId)) return 'consult';
        if (item.inform?.some(i => i.key === currentUserId)) return 'inform';
        return 'none';
    };


    const handleRefresh = () => {
        setPageIndex(0);
        setAllGenerals([]);
        setHasMore(true);
        const payload = {
            includeCompleted,
            sort: sortField,
            order: sortOrder,
            pageIndex: 0,
            pageSize,
            filterKey: searchText
        };

        if (appliedResourceTypes.length > 0) {
            payload.resourceTypes = appliedResourceTypes;
        }

        dispatch(getList(payload));
    };

    // Initial load and sort change
    useEffect(() => {
        setPageIndex(0);
        setAllGenerals([]);
        setHasMore(true);
        const payload = {
            includeCompleted: includeCompleted,
            sort: sortField,
            order: sortOrder,
            pageIndex: 0,
            pageSize: pageSize,
            filterKey: searchText
        };

        if (appliedResourceTypes.length > 0) {
            payload.resourceTypes = appliedResourceTypes;
        }

        dispatch(getList(payload)).finally(() => {
            setSortLoading(false);
        });
    }, [dispatch, includeCompleted, searchText, sortField, sortOrder, appliedResourceTypes]);

    // Accumulate generals when new data arrives
    useEffect(() => {
        if (generalList && generalList) {
            if (pageIndex === 0 || isRefreshingRef.current) {
                setAllGenerals(generalList);
                isRefreshingRef.current = false;
            } else {
                setAllGenerals((prev) => [...prev, ...generalList]);
            }
            setHasMore(generalList.length === pageSize);
        }
    }, [generalList, pageIndex, pageSize]);

    // Auto-refresh every 20 seconds when page is active
    useEffect(() => {
        if (!hasValidTokens()) {
            return;
        }

        const interval = setInterval(() => {
            const currentLength = allGenerals.length || pageSize;
            isRefreshingRef.current = true;
            dispatch(getList({
                includeCompleted: includeCompleted,
                sort: sortField,
                order: sortOrder,
                pageIndex: 0,
                pageSize: currentLength,
                filterKey: searchText,
                silent: true,
                resourceTypes: appliedResourceTypes
            }));
        }, 20000);

        return () => clearInterval(interval);
    }, [dispatch, includeCompleted, searchText, allGenerals.length, pageSize, sortField, sortOrder, appliedResourceTypes]);

    // Load more generals
    const loadMoreGenerals = useCallback(() => {
        if (!loading && hasMore) {
            const nextPage = pageIndex + 1;
            setPageIndex(nextPage);
            dispatch(getList({
                includeCompleted: includeCompleted,
                sort: sortField,
                order: sortOrder,
                pageIndex: nextPage,
                pageSize,
                filterKey: searchText,
                resourceTypes: appliedResourceTypes
            }));
        }
    }, [loading, hasMore, pageIndex, dispatch, includeCompleted, sortField, sortOrder, searchText, pageSize, appliedResourceTypes]);

    // Infinite scroll handler
    const handleScroll = useCallback((e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;

        // Load more when scrolled to 80% of content
        if (scrollHeight - scrollTop <= clientHeight * 1.2) {
            loadMoreGenerals();
        }
    }, [loadMoreGenerals]);


    const menuItems = (item) => {
        const role = getUserRole(item, currentUser);
        const isCompleted = item.status === "Completed";

        const baseItems = [
            {
                key: "details",
                text: "Details",
                onClick: () => {
                    setIsGeneralDetailsOpen(true);
                    setIsMenuOpen(false);
                    setid(item.id);
                },
            }
        ];

        if (!isCompleted) {
            baseItems.push({
                key: "comment",
                text: "Comment",
                onClick: () => {
                    setIsCommentOpen(true);
                    setIsMenuOpen(false);
                    setid(item.id);
                },
            });

            if (role === 'creator' || role === 'responsible') {
                baseItems.push(
                    {
                        key: "edit",
                        text: "Edit",
                        onClick: () => {
                            setid(item.id);
                            setIsDrawerOpen(true);
                            setIsMenuOpen(false);
                        },
                    }
                );
            }

            if (['accountable', 'consult', 'inform'].includes(role)) {
            }
        }

        if (isCompleted && role === 'creator') {
            baseItems.push({
                key: "changeStatus",
                text: "Mark as Pending",
                onClick: () => handleChangeStatus(item.id)
            });
        }

        return baseItems;
    };

    const handleChangeStatus = (id) => {
        dispatch(editStatus("/workitems", {
            id: id,
            status: 1
        })).then(() => {
            setIsMenuOpen(false);
            handleRefresh();
        });
    };


    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
        });
    };

    const formatCreatedOn = (dateStr) => {
        if (!dateStr) return '';

        const date = new Date(dateStr + 'Z');

        return date.toLocaleString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
    };


    const handleSort = (field) => {
        let newSortOrder = "asc";
        if (sortField === field) {
            newSortOrder = sortOrder === "asc" ? "desc" : "asc";
        }

        setSortField(field);
        setSortOrder(newSortOrder);
    };

    const hasActiveFilters = searchText || (appliedResourceTypes && appliedResourceTypes.length > 0);
    const capitalize = (str) => {
        if (!str) return "";
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    };


    const handleApplyFilters = () => {
        setSearchText(searchValue);
        setAppliedResourceTypes(filterResourceTypes);
        setIsFilterMenuOpen(false);
    };

    const handleClearFilters = () => {
        setAppliedResourceTypes([]);
        setFilterResourceTypes([]);
        setSearchValue("");
        setSearchText("");
        setIsFilterMenuOpen(false);
    };


    const handleCancelFilters = () => {
        setIsFilterMenuOpen(false);
    };

    return (
        <div className="teams-card">
            <div className='page-action-header'>
                <Stack horizontal tokens={{ childrenGap: 16 }} styles={{ root: { width: "100%", display: "flex", alignItems: "center" } }}>
                    <Stack horizontal styles={{ root: { position: 'relative' } }}>
                        <SearchBox
                            style={{ width: '300px' }}
                            placeholder="Search here"
                            value={searchValue}
                            onChange={(_, newValue) => {
                                const limited = (newValue ?? '').slice(0, 40);
                                setSearchValue(limited);
                                if ((newValue ?? '').trim() === '') {
                                    setSearchText('');
                                }
                            }}
                            onSearch={newValue => setSearchText(newValue || '')}
                            showIcon={true}
                            disableAnimation={true}
                            styles={{
                                clearButton: { display: 'none' }
                            }}
                        />
                        <div ref={filterButtonRef} style={{ position: 'relative' }}>
                            <IconButton
                                iconProps={{ iconName: 'Filter' }}
                                title="Filter"
                                ariaLabel="Filter"
                                onClick={() => {
                                    setFilterResourceTypes(appliedResourceTypes);
                                    setIsFilterMenuOpen(!isFilterMenuOpen);
                                }}
                                styles={{
                                    root: {
                                        position: 'absolute',
                                        right: '8px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        zIndex: 1
                                    }
                                }}
                            />
                            {hasActiveFilters && (
                                <span
                                    style={{
                                        position: 'absolute',
                                        top: '8px',
                                        right: '10px',
                                        width: '8px',
                                        height: '8px',
                                        backgroundColor: '#dc2626',
                                        borderRadius: '50%',
                                        zIndex: 2,
                                        border: '1px solid white'
                                    }}
                                />
                            )}
                        </div>
                    </Stack>
                    <Stack>
                        <Checkbox label="Show Completed" checked={includeCompleted}
                            onChange={(_, checked) => setincludeCompleted(!!checked)} />
                    </Stack>
                </Stack>

                <div>
                    <PrimaryButton
                        text="Add Task"
                        type="button"
                        onClick={toggleDrawer}
                    />
                </div>
            </div>



            <div
                ref={scrollContainerRef}
                onScroll={handleScroll}
                className="table-container"
            >
                <table>
                    <thead>
                        <tr>
                            <th className="cursor-pointer" style={{ width: "40%", maxWidth: "40%" }} onClick={() => handleSort("title")}>
                                Title {sortField === "title" && (sortOrder === "asc" ? "↑" : "↓")}
                            </th>
                            <th className="cursor-pointer" onClick={() => handleSort("assignmentType")}>
                                Assignment Type {sortField === "assignmentType" && (sortOrder === "asc" ? "↑" : "↓")}
                            </th>
                            <th className="cursor-pointer" onClick={() => handleSort("startDate")}>
                                Start Date{sortField === "startDate" && (sortOrder === "asc" ? "↑" : "↓")}
                            </th>
                            <th className="cursor-pointer" onClick={() => handleSort("endDate")}>
                                End Date {sortField === "endDate" && (sortOrder === "asc" ? "↑" : "↓")}
                            </th>
                            <th className="cursor-pointer" onClick={() => handleSort("createdOn")}>
                                Created On {" "}
                                {(sortField === "createdOn" || !sortField) && (
                                    sortOrder === "asc" ? "↑" : "↓"
                                )}
                            </th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {allGenerals && allGenerals.length > 0 ? (
                            allGenerals.map((item, idx) => (
                                <tr
                                    key={item.id}
                                    style={{ cursor: "pointer" }}
                                    onClick={() => {
                                        setid(item.id);
                                        setIsGeneralDetailsOpen(true);
                                    }}
                                >
                                    <td>
                                        <div style={{ display: "flex", alignItems: "center", maxWidth: "100%" }}>
                                            <div style={{ display: "flex", flexShrink: 0 }}>
                                                {item.createdByUser === true ? (
                                                    <Icon iconName="ArrowUpRight" />
                                                ) : (
                                                    <Icon iconName="ArrowUpRight" className="rotate-180" />
                                                )}
                                            </div>
                                            <TooltipHost content={item.title}>
                                                <div
                                                    style={{
                                                        marginLeft: "10px",
                                                        whiteSpace: "nowrap",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        maxWidth: "25vw"
                                                    }}
                                                >
                                                    {item.title}
                                                </div>
                                            </TooltipHost>
                                            {item.hasComments === true && (
                                                <div style={{ display: "flex", marginLeft: "10px", cursor: "pointer" }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setid(item.id);
                                                        setIsCommentOpen(true);
                                                    }}>
                                                    <Icon iconName="Message" />
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        {item.assignmentType && item.assignmentType.trim() !== ""
                                            ? capitalize(item.assignmentType)
                                            : "-"}
                                        </td>
                                    <td>{formatDate(item.startDate)}</td>
                                    <td>{formatDate(item.endDate)}</td>
                                    <td>{formatCreatedOn(item.createdOn)}</td>
                                    <td onClick={(e) => e.stopPropagation()}>
                                        <IconButton
                                            className="color-black"
                                            iconProps={{ iconName: "MoreVertical" }}
                                            ariaLabel="More options"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setTarget(e.currentTarget);
                                                setIsMenuOpen(true);
                                                setSelectedRowId(item.id);
                                            }}
                                        />
                                        {isMenuOpen && selectedRowId === item.id && (
                                            <ContextualMenu
                                                items={menuItems(item)}
                                                target={target}
                                                onDismiss={() => setIsMenuOpen(false)}
                                            />
                                        )}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            !loading && (
                                <tr>
                                    <td colSpan="6" className="text-center">
                                        No task found
                                    </td>
                                </tr>
                            )
                        )}
                    </tbody>

                </table>

                {loading && pageIndex > 0 && (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <Spinner size={SpinnerSize.medium} label="Loading more tasks..." />
                    </div>
                )}
            </div>

            {isDrawerOpen && <AddGeneral closeDrawer={closeDrawer} id={id} onRefresh={handleRefresh} userRole={id ? getUserRole(allGenerals?.find(item => item.id === id), currentUser) : null} />}
            {isGeneralDetailsOpen && <GeneralDetails closeDrawer={closeDrawer} id={id} />}
            {isCommentOpen && <GeneralComment closeDrawer={closeDrawer} id={id} isCompleted={id ? allGenerals?.find(item => item.id === id)?.status === "Completed" : false} />}

            {isFilterMenuOpen && (
                <Callout
                    className="filter-callout"
                    target={filterButtonRef.current}
                    onDismiss={() => setIsFilterMenuOpen(false)}
                    directionalHint={DirectionalHint.bottomRightEdge}
                    isBeakVisible={false}
                >
                    <Stack tokens={{ childrenGap: 16 }}>
                        <SearchBox
                            placeholder="Search here"
                            value={searchValue}
                            onChange={(_, newValue) => {
                                const limited = (newValue ?? '').slice(0, 40);
                                setSearchValue(limited);
                            }}
                        />

                        <div>
                            <label style={{ fontWeight: 600 }}>Assignment Type</label>

                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    columnGap: "100px",
                                    rowGap: "16px",
                                    marginTop: "10px"
                                }}
                            >
                                {resourceTypeOptions.map(opt => (
                                    <Checkbox
                                        key={opt.key}
                                        label={opt.text}
                                        checked={filterResourceTypes.includes(opt.key)}
                                        onChange={(_, checked) => {
                                            if (checked) {
                                                setFilterResourceTypes(prev => [...prev, opt.key]);
                                            } else {
                                                setFilterResourceTypes(prev =>
                                                    prev.filter(x => x !== opt.key)
                                                );
                                            }
                                        }}
                                    />
                                ))}
                            </div>
                        </div>



                        <Separator />

                        <Stack horizontal tokens={{ childrenGap: 10 }} styles={{ root: { justifyContent: 'space-between' } }}>
                            <DefaultButton
                                className="btn-sm"
                                text="Clear All"
                                onClick={handleClearFilters}
                            />
                            <Stack horizontal tokens={{ childrenGap: 10 }}>
                                <DefaultButton
                                    className="btn-sm"
                                    text="Cancel"
                                    onClick={handleCancelFilters}
                                />
                                <PrimaryButton
                                    className="btn-sm"
                                    text="Apply"
                                    onClick={handleApplyFilters}
                                />
                            </Stack>
                        </Stack>
                    </Stack>
                </Callout>
            )}
        </div>
    );
};

export default GeneralManage;
